<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue";
import { clickOutSide as vClickOutSide } from "@mahdikhashan/vue3-click-outside";
import { useRoute } from "vue-router";
import { useNotificationStore } from "@/stores/NotificationStore";
import { GetNotifications } from "@/services/notification";
import Skeleton from "primevue/skeleton";
import Button from "primevue/button";

const route = useRoute();
const notificationStore = useNotificationStore();

const open = ref(false);
const items = ref([]);
const loading = ref(false);

const hide = () => {
  open.value = false;
};

watch(() => route.path, hide);

const badge = computed(() => (notificationStore.count > 99 ? "99+" : notificationStore.count));

// Колокольчик «звенит», когда опрос приносит новые уведомления. Только на рост
// счётчика: сброс после прочтения анимацию не вызывает.
const ringing = ref(false);
let ringTimer = null;

watch(
  () => notificationStore.count,
  (val, prev) => {
    if (!(val > (prev ?? 0))) return;
    ringing.value = false;
    clearTimeout(ringTimer);
    // кадр пропускаем, чтобы анимация перезапустилась, если класс ещё висел
    requestAnimationFrame(() => {
      ringing.value = true;
      ringTimer = setTimeout(() => (ringing.value = false), 1000);
    });
  },
);

onBeforeUnmount(() => clearTimeout(ringTimer));

// Список перезапрашивается на каждое открытие. Без счётчика быстрое
// закрытие-открытие оставляло два запроса в полёте: первый завершившийся
// снимал загрузку и мог перезаписать список более старыми данными.
let fetchId = 0;

watch(open, async (val) => {
  if (!val) return;

  const current = ++fetchId;
  loading.value = true;
  try {
    const response = await GetNotifications(5);
    if (current !== fetchId) return;

    items.value = response?.data?.items ?? [];
  } catch {
    if (current !== fetchId) return;
    items.value = [];
  } finally {
    if (current === fetchId) loading.value = false;
  }
});
</script>

<template>
  <!-- v-click-out-side закрывает только мышью: с клавиатуры выпадашку было
       не закрыть, поэтому добавлен Escape -->
  <li v-click-out-side="hide" class="page-header__nav-item relative" @keydown.esc="hide">
    <!-- icon-проп здесь не работал: PrimeVue рисует его только как запасной
         вариант, когда слот по умолчанию пуст, а он занят иконкой ниже -->
    <Button
      :aria-label="$t('notifications')"
      :aria-expanded="open"
      class="relative flex h-9 w-9 items-center justify-center overflow-visible !rounded-full !p-0 text-lg transition-colors"
      :class="open ? '!bg-blue-50 !text-[#2e71e5]' : 'text-gray-600 hover:!bg-gray-100'"
      text
      @click.prevent="open = !open"
    >
      <i class="pi pi-bell" :class="{ 'bell-ring': ringing }"></i>
      <!-- было 8px (text-[.5rem]) в круге 16px — «99+» читалось с трудом.
           Белое кольцо отделяет бейдж от иконки и от фона шапки. -->
      <span
        v-if="notificationStore.count > 0"
        class="pointer-events-none absolute -right-0.5 -top-0.5 z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white shadow-sm ring-2 ring-white"
      >
        {{ badge }}
      </span>
    </Button>

    <Transition name="bell-dropdown">
      <div v-if="open" class="page-header__dropdown page-header__dropdown--show">
        <div class="header-dropdown bell-panel overflow-hidden">
          <!-- Счётчик здесь не дублируем: выпадашка висит под кнопкой, её бейдж
               остаётся на экране, а второй такой же цифрой другого цвета. -->
          <div class="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <h4 class="ui-kit__h4 header-dropdown__title">
              {{ $t("notifications") }}
            </h4>
            <router-link
              to="/notifications"
              class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 no-underline transition-colors hover:bg-gray-100 hover:text-[#2e71e5]"
              :aria-label="$t('show_all')"
              :title="$t('show_all')"
              @click="hide"
            >
              <i class="pi pi-external-link text-sm" />
            </router-link>
          </div>

          <!-- Прокручивается только список: шапка и ссылка «показать все» остаются
               на месте. 60vh вместо фиксированной высоты — на невысоких экранах
               выпадашка не уезжает за пределы окна, а при коротком списке скролла
               не появляется вообще. overscroll-contain не даёт прокрутке
               перескочить на страницу, когда список докрутили до конца. -->
          <div class="max-h-[60vh] overflow-y-auto overscroll-contain">
            <!-- Скелет повторяет геометрию строки: аватар слева, две строки
                 текста и мета-строка снизу, — чтобы список не прыгал. -->
            <div v-if="loading" class="flex flex-col py-1">
              <div v-for="n in 3" :key="n" class="flex gap-3 px-5 py-3">
                <Skeleton shape="circle" size="2.25rem" class="shrink-0" />
                <div class="min-w-0 flex-1">
                  <Skeleton height="0.75rem" />
                  <Skeleton height="0.75rem" width="70%" class="mt-1.5" />
                  <div class="mt-2 flex items-center justify-between gap-2">
                    <Skeleton height="0.625rem" width="4rem" />
                    <Skeleton height="0.625rem" width="1.75rem" />
                  </div>
                </div>
              </div>
            </div>

            <template v-else-if="items.length">
              <section v-for="(group, i) in items" :key="i">
                <!-- Заголовок периода, как на странице уведомлений. Липкий, чтобы
                     при прокрутке было видно, к какому дню относятся строки. -->
                <h5
                  v-if="group.period || group.date"
                  class="sticky top-0 z-[1] m-0 bg-white/95 px-5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 backdrop-blur"
                >
                  {{ group.period ? $t(group.period) : group.date }}
                </h5>

                <div
                  v-for="(item, j) in group.items"
                  :key="item.uuid ?? j"
                  class="header-dropdown-item relative flex gap-3 px-5 py-3 transition-colors hover:bg-gray-50 motion-reduce:transition-none"
                  :class="item.is_viewed ? '' : 'bg-blue-50/50'"
                >
                  <!-- Полоса во всю высоту строки у непрочитанных, как на
                       странице; позиционирована абсолютно и текст не сдвигает. -->
                  <span
                    v-if="!item.is_viewed"
                    class="absolute inset-y-0 left-0 w-[3px] rounded-r bg-[#2e71e5]"
                  />

                  <span
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                    :class="
                      item.is_viewed ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-[#2e71e5]'
                    "
                  >
                    <i class="pi pi-bell text-sm" />
                  </span>

                  <!-- ширину держат min-w-0 + flex-1: max-w здесь был больше,
                       чем реально доступно в 380px выпадашке, и ничего не делал -->
                  <div class="min-w-0 flex-1">
                    <!-- v-html обязателен: nrk-notification отдаёт готовый HTML со
                         ссылкой. Значения параметров шаблона там не экранируются —
                         подробности в views/profile/Notifications.vue. -->
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                      class="bell-text m-0 line-clamp-2 text-[13px] leading-snug"
                      :class="item.is_viewed ? 'text-gray-600' : 'font-medium text-gray-800'"
                      v-html="item.notification"
                    />
                    <!-- eslint-enable vue/no-v-html -->

                    <!-- Категория раньше была только на странице: в смешанном
                         списке без неё непонятно, к чему относится уведомление.
                         Время справа на той же строке, без точки-разделителя. -->
                    <div
                      v-if="item.group?.title || item.time"
                      class="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-gray-400"
                    >
                      <span
                        v-if="item.group?.title"
                        class="truncate rounded bg-gray-100 px-1.5 py-0.5 text-gray-500"
                      >
                        {{ item.group.title }}
                      </span>
                      <time
                        v-if="item.time"
                        class="ml-auto flex shrink-0 items-center gap-1 tabular-nums"
                      >
                        <i class="pi pi-clock text-[10px]" />
                        {{ item.time }}
                      </time>
                    </div>
                  </div>
                </div>
              </section>
            </template>

            <!-- text-gray-300 в этом проекте переопределён в #F6F8F9 — иконка
                 пустого состояния на белом была почти не видна -->
            <div v-else class="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <span class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <i class="pi pi-bell text-xl text-gray-400" />
              </span>
              <span class="text-[13px] text-gray-500">{{ $t("no_data") }}</span>
            </div>
          </div>

          <router-link
            to="/notifications"
            class="group flex items-center justify-center gap-1.5 border-t border-gray-100 px-6 py-3 text-sm font-medium text-[#2e71e5] no-underline transition-colors hover:bg-gray-50"
            @click="hide"
          >
            {{ $t("show_all") }}
            <i class="pi pi-arrow-right text-xs transition-transform group-hover:translate-x-0.5" />
          </router-link>
        </div>
      </div>
    </Transition>
  </li>
</template>

<style scoped>
/* Мягкая тень вместо глобальной 0 0 16px rgba(0,0,0,.3) — та давала грязный
   ореол вокруг выпадашки */
.bell-panel {
  border: 1px solid rgb(0 0 0 / 0.06);
  box-shadow:
    0 10px 30px -8px rgb(15 23 42 / 0.2),
    0 4px 10px -4px rgb(15 23 42 / 0.08);
}

/* Ссылки внутри HTML от nrk-notification */
.bell-text :deep(a) {
  color: #2e71e5;
  text-decoration: none;
}
.bell-text :deep(a:hover) {
  text-decoration: underline;
}

.bell-dropdown-enter-active,
.bell-dropdown-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  transform-origin: top right;
}
.bell-dropdown-enter-from,
.bell-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.bell-ring {
  display: inline-block;
  transform-origin: top center;
  animation: bell-ring 1s ease-in-out;
}
@keyframes bell-ring {
  0%,
  100% {
    transform: rotate(0);
  }
  15% {
    transform: rotate(14deg);
  }
  30% {
    transform: rotate(-12deg);
  }
  45% {
    transform: rotate(8deg);
  }
  60% {
    transform: rotate(-6deg);
  }
  75% {
    transform: rotate(2deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bell-dropdown-enter-active,
  .bell-dropdown-leave-active {
    transition: none;
  }
  .bell-ring {
    animation: none;
  }
}
</style>
